export const PACK_IMAGE_QUALITY = 100

export function isPackImage(image) {
  return (
    Boolean(image) && typeof image === 'object' && image.is_pack_image === true
  )
}

/**
 * Accepts either an upload — which may carry a template — or the bare address of one,
 * so that a caller passing `image.url` keeps working while the callers that pass the
 * upload itself get the resized copy from the image service.
 */
function resolveUpload(image, extension) {
  if (image && typeof image === 'object') {
    return {
      url: image.url,
      template: image.url_template,
      extension: image.extension ?? extension,
    }
  }

  return { url: image, template: null, extension }
}

/**
 * Fill in the dimensions of an address handed out by the API.
 *
 * How such an address is written is deliberately not our business: an upload served by
 * a dedicated image service carries the shape of it in `url_template`, and all that is
 * left to do here is say how big. A dimension of `0` is free and gets computed from
 * the other one, which is what makes one template answer both a crop and a fit.
 */
function fromTemplate(template, width, height, quality) {
  return template
    .replace('{w}', Math.round(width) || 0)
    .replace('{h}', Math.round(height) || 0)
    .replace('{q}', Math.round(quality) || 0)
}

/**
 * Spell the transformation out in the file name, the way an upload served straight off
 * a filesystem expects it.
 */
function inFilename(url, extension, dimensions, quality) {
  if (
    !url ||
    url.endsWith('.gif') ||
    url.includes(`.${extension}.${extension}`)
  ) {
    return url
  }
  if (!url.includes(`.${extension}`)) {
    return undefined
  }

  const suffix = quality
    ? `-${dimensions}-quality(${quality})`
    : `-${dimensions}`

  return url.replace(`.${extension}`, `${suffix}.${extension}`)
}

export function cropUrl(image, width, height, extension, quality = 86) {
  const upload = resolveUpload(image, extension)
  const chosen = isPackImage(image) ? PACK_IMAGE_QUALITY : quality

  if (upload.template) {
    return fromTemplate(upload.template, width, height, chosen)
  }

  return inFilename(upload.url, upload.extension, `${width}x${height}`, chosen)
}
